import { useState, useEffect, useRef } from "react";

const gold = "#f5a623", blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e", red = "#ef4444", purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const s = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

// ── Three Statements Overview ──
function StatementsOverview() {
  const [active, setActive] = useState(0);
  const stmts = [
    {
      icon: "🏛️", label: "Balance Sheet", fullName: "Statement of Financial Position", color: blue2,
      when: "At a specific point in time (e.g., December 31)",
      answers: "What does the company own? What does it owe? What is its net worth?",
      equation: "Assets = Liabilities + Shareholders' Equity",
      sections: [
        { name: "Assets", items: ["Current Assets (cash, receivables, inventory)", "Fixed / Non-current Assets (factories, equipment, land)", "Less: Accumulated Depreciation"], color: green },
        { name: "Liabilities", items: ["Current Liabilities (payables, taxes, wages due < 1 yr)", "Long-Term Liabilities (mortgages, bonds, bank loans)"], color: red },
        { name: "Shareholders' Equity", items: ["Common Shares (capital contributed by investors)", "Retained Earnings (accumulated profits not paid as dividends)"], color: gold },
      ],
    },
    {
      icon: "📊", label: "Income Statement", fullName: "Statement of Comprehensive Income", color: gold,
      when: "Over a period of time (e.g., the year ended December 31)",
      answers: "Is the company profitable? How much did it earn after all costs?",
      equation: "Gross Profit = Revenue − Cost of Sales → Net Profit = Gross Profit − Expenses − Taxes",
      sections: [
        { name: "Revenue", items: ["Total sales of goods or services"], color: green },
        { name: "Cost of Sales", items: ["Direct production costs: labour, raw materials"], color: red },
        { name: "Gross Profit", items: ["Revenue minus Cost of Sales"], color: blue2 },
        { name: "Expenses", items: ["Rent, salaries, depreciation, interest on debt"], color: red },
        { name: "Net Profit", items: ["Gross Profit minus all expenses and taxes — the 'bottom line'"], color: gold },
      ],
    },
    {
      icon: "🔗", label: "Changes in Equity", fullName: "Statement of Changes in Equity", color: green,
      when: "Over the same period as the income statement",
      answers: "How did retained earnings change? What was paid in dividends?",
      equation: "Opening Retained Earnings + Net Profit − Dividends Paid = Closing Retained Earnings",
      sections: [
        { name: "Opening Retained Earnings", items: ["Prior year-end balance of accumulated profits"], color: blue2 },
        { name: "Add: Net Profit", items: ["From the current period's Income Statement"], color: green },
        { name: "Less: Dividends Paid", items: ["Cash distributed to shareholders during the period"], color: red },
        { name: "Closing Retained Earnings", items: ["This figure flows directly onto the Balance Sheet"], color: gold },
      ],
    },
  ];
  const stmt = stmts[active];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {stmts.map((st, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "10px 18px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? st.color + "44" : border}`, borderBottom: active === i ? `2px solid ${st.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? st.color : muted, transition: "all 0.2s", whiteSpace: "nowrap" }}>
            {st.icon} {st.label}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${stmt.color}22`, borderRadius: "0 0 8px 8px", padding: 26 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.15rem", color: white, marginBottom: 4 }}>{stmt.fullName}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          {[["When", stmt.when], ["Answers", stmt.answers]].map(([lbl, val], i) => (
            <div key={i} style={{ background: navy3, borderRadius: 6, padding: "10px 14px" }}>
              <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 4 }}>{lbl}</div>
              <div style={{ fontSize: "0.83rem", color: textCol }}>{val}</div>
            </div>
          ))}
        </div>
        <div style={{ fontFamily: "monospace", fontSize: "0.8rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "8px 14px", marginBottom: 16 }}>{stmt.equation}</div>
        <div style={{ display: "grid", gap: 8 }}>
          {stmt.sections.map((sec, i) => (
            <div key={i} style={{ background: navy3, borderLeft: `3px solid ${sec.color}`, borderRadius: 6, padding: "12px 16px" }}>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: sec.color, marginBottom: 6 }}>{sec.name}</div>
              {sec.items.map((it, j) => <div key={j} style={{ fontSize: "0.83rem", color: textCol, marginBottom: 2 }}>→ {it}</div>)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Interactive Balance Sheet ──
function BalanceSheet() {
  const assets = [
    { label: "Cash", value: 20000, type: "current", color: green },
    { label: "Trade Receivables", value: 40000, type: "current", color: green },
    { label: "Inventories", value: 60000, type: "current", color: green },
    { label: "Equipment (Net)", value: 180000, type: "fixed", color: blue2 },
    { label: "Land & Buildings", value: 120000, type: "fixed", color: blue2 },
  ];
  const liabilities = [
    { label: "Trade Payables", value: 50000, type: "current", color: red },
    { label: "Wages Payable", value: 15000, type: "current", color: red },
    { label: "Taxes Payable", value: 15000, type: "current", color: red },
    { label: "Long-Term Bonds", value: 150000, type: "longterm", color: purple },
    { label: "Bank Mortgage", value: 50000, type: "longterm", color: purple },
  ];
  const equity = [
    { label: "Common Shares", value: 90000, color: gold },
    { label: "Retained Earnings", value: 50000, color: gold },
  ];

  const totalAssets = assets.reduce((s, a) => s + a.value, 0);
  const totalLiab = liabilities.reduce((s, l) => s + l.value, 0);
  const totalEquity = equity.reduce((s, e) => s + e.value, 0);

  function Row({ label, value, color, indent = false }) {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${border}`, paddingLeft: indent ? 14 : 0 }}>
        <span style={{ fontSize: "0.83rem", color: textCol }}>{label}</span>
        <span style={{ fontFamily: "monospace", fontSize: "0.83rem", color: color || textCol }}>${value.toLocaleString()}</span>
      </div>
    );
  }

  function Section({ title, accent, children, total, totalLabel }) {
    return (
      <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "16px 18px", marginBottom: 10 }}>
        <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: accent, marginBottom: 10 }}>{title}</div>
        {children}
        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, marginTop: 4, borderTop: `1px solid ${accent}44` }}>
          <span style={{ fontSize: "0.82rem", fontWeight: 700, color: accent }}>{totalLabel || `Total ${title}`}</span>
          <span style={{ fontFamily: "monospace", fontSize: "0.85rem", fontWeight: 700, color: accent }}>${total.toLocaleString()}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "0.9rem", color: muted, marginBottom: 16, textAlign: "center" }}>
        XYZ Inc. — Statement of Financial Position (Balance Sheet)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {/* Assets column */}
        <div>
          <Section title="Current Assets" accent={green} total={assets.filter(a => a.type === "current").reduce((s, a) => s + a.value, 0)} totalLabel="Total Current Assets">
            {assets.filter(a => a.type === "current").map((a, i) => <Row key={i} label={a.label} value={a.value} color={a.color} />)}
          </Section>
          <Section title="Fixed Assets" accent={blue2} total={assets.filter(a => a.type === "fixed").reduce((s, a) => s + a.value, 0)} totalLabel="Total Fixed Assets">
            {assets.filter(a => a.type === "fixed").map((a, i) => <Row key={i} label={a.label} value={a.value} color={a.color} />)}
          </Section>
          <div style={{ background: navy4, border: `1px solid ${blue2}55`, borderRadius: 8, padding: "12px 18px", display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontFamily: "Georgia,serif", fontSize: "0.9rem", fontWeight: 700, color: white }}>TOTAL ASSETS</span>
            <span style={{ fontFamily: "monospace", fontSize: "0.95rem", fontWeight: 700, color: blue2 }}>${totalAssets.toLocaleString()}</span>
          </div>
        </div>
        {/* Liabilities + Equity column */}
        <div>
          <Section title="Current Liabilities" accent={red} total={liabilities.filter(l => l.type === "current").reduce((s, l) => s + l.value, 0)} totalLabel="Total Current Liabilities">
            {liabilities.filter(l => l.type === "current").map((l, i) => <Row key={i} label={l.label} value={l.value} color={l.color} />)}
          </Section>
          <Section title="Long-Term Liabilities" accent={purple} total={liabilities.filter(l => l.type === "longterm").reduce((s, l) => s + l.value, 0)} totalLabel="Total Long-Term Liabilities">
            {liabilities.filter(l => l.type === "longterm").map((l, i) => <Row key={i} label={l.label} value={l.value} color={l.color} />)}
          </Section>
          <Section title="Shareholders' Equity" accent={gold} total={totalEquity} totalLabel="Total Equity">
            {equity.map((e, i) => <Row key={i} label={e.label} value={e.value} color={e.color} />)}
          </Section>
          <div style={{ background: navy4, border: `1px solid ${gold}55`, borderRadius: 8, padding: "12px 18px", display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontFamily: "Georgia,serif", fontSize: "0.9rem", fontWeight: 700, color: white }}>TOTAL L + EQUITY</span>
            <span style={{ fontFamily: "monospace", fontSize: "0.95rem", fontWeight: 700, color: gold }}>${(totalLiab + totalEquity).toLocaleString()}</span>
          </div>
        </div>
      </div>
      {totalAssets === totalLiab + totalEquity && (
        <div style={{ textAlign: "center", marginTop: 12, fontSize: "0.8rem", color: green }}>✓ Balance sheet balances — Assets (${totalAssets.toLocaleString()}) = Liabilities + Equity (${(totalLiab + totalEquity).toLocaleString()})</div>
      )}
    </div>
  );
}

// ── Ratio Explorer ──
const ratioGroups = [
  {
    id: "liquidity", icon: "💧", label: "Liquidity", color: blue2,
    desc: "Can the company pay its short-term debts?",
    ratios: [
      { name: "Current Ratio", formula: "Current Assets ÷ Current Liabilities", benchmark: "Good: 2:1", benchmarkColor: blue2, calc: (d) => (d.currentAssets / d.currentLiabilities).toFixed(2), unit: ":1", interpret: (v) => v >= 2 ? "Strong liquidity — well above the 2:1 benchmark." : v >= 1 ? "Adequate but below ideal. Watch closely." : "Below 1:1 — may struggle to meet short-term obligations." },
      { name: "Quick Ratio (Acid Test)", formula: "(Current Assets − Inventory) ÷ Current Liabilities", benchmark: "Good: 1:1", benchmarkColor: blue2, calc: (d) => ((d.currentAssets - d.inventory) / d.currentLiabilities).toFixed(2), unit: ":1", interpret: (v) => v >= 1 ? "Strong quick liquidity — can cover debts without selling inventory." : "Below 1:1 — relies on inventory sales to cover immediate debts. Riskier." },
    ],
  },
  {
    id: "risk", icon: "⚠️", label: "Risk / Leverage", color: red,
    desc: "Can the company handle its debt load?",
    ratios: [
      { name: "Debt / Equity Ratio", formula: "Total Debt ÷ Total Equity", benchmark: "Lower is safer", benchmarkColor: green, calc: (d) => (d.totalDebt / d.totalEquity).toFixed(2), unit: "x", interpret: (v) => v <= 0.5 ? "Conservative leverage — well-funded by equity." : v <= 1 ? "Moderate leverage." : "High leverage — more debt than equity. Higher financial risk." },
      { name: "Interest Coverage Ratio", formula: "EBIT ÷ Interest Expense", benchmark: "Higher is better (>3 is good)", benchmarkColor: green, calc: (d) => (d.ebit / d.interestExpense).toFixed(2), unit: "x", interpret: (v) => v >= 3 ? "Strong coverage — earnings comfortably cover interest." : v >= 1.5 ? "Adequate but thin margin. Monitor carefully." : "Dangerously low — earnings barely cover interest." },
    ],
  },
  {
    id: "operating", icon: "⚙️", label: "Operating Performance", color: gold,
    desc: "How efficiently is management using resources?",
    ratios: [
      { name: "Gross Profit Margin", formula: "Gross Profit ÷ Revenue × 100", benchmark: "Higher = more efficient production", benchmarkColor: gold, calc: (d) => ((d.grossProfit / d.revenue) * 100).toFixed(1), unit: "%", interpret: (v) => `${v}% — the company keeps ${v} cents of every revenue dollar after direct production costs.` },
      { name: "Net Profit Margin", formula: "Net Profit ÷ Revenue × 100", benchmark: "Higher = more profitable overall", benchmarkColor: gold, calc: (d) => ((d.netProfit / d.revenue) * 100).toFixed(1), unit: "%", interpret: (v) => `${v}% — the company retains ${v} cents of every revenue dollar as profit after all costs.` },
      { name: "Return on Equity (ROE)", formula: "Net Profit ÷ Total Equity × 100", benchmark: "Higher = better use of shareholders' money", benchmarkColor: gold, calc: (d) => ((d.netProfit / d.totalEquity) * 100).toFixed(1), unit: "%", interpret: (v) => `${v}% return on shareholders' equity.` },
      { name: "Inventory Turnover", formula: "Cost of Sales ÷ Inventory", benchmark: "Higher = selling goods faster", benchmarkColor: green, calc: (d) => (d.costOfSales / d.inventory).toFixed(1), unit: "x / yr", interpret: (v) => `${v} times per year — the company sells through its entire inventory approximately ${v} times annually.` },
    ],
  },
  {
    id: "value", icon: "💎", label: "Value", color: purple,
    desc: "What is the stock worth relative to its earnings?",
    ratios: [
      { name: "Earnings Per Share (EPS)", formula: "Net Profit ÷ Shares Outstanding", benchmark: "Higher = more profit per share", benchmarkColor: purple, calc: (d) => (d.netProfit / d.shares).toFixed(2), unit: "$/share", interpret: (v) => `$${v} of earnings for every share outstanding.` },
      { name: "Price-Earnings Ratio (P/E)", formula: "Market Price ÷ EPS", benchmark: "Investors pay $X per $1 of earnings", benchmarkColor: purple, calc: (d) => { const eps = d.netProfit / d.shares; return (d.stockPrice / eps).toFixed(1); }, unit: "x", interpret: (v) => `Investors pay $${v} for every $1 of company earnings.` },
      { name: "Dividend Yield", formula: "(Annual Dividend Per Share ÷ Market Price) × 100", benchmark: "Higher = more income per dollar invested", benchmarkColor: gold, calc: (d) => { const dps = d.totalDividends / d.shares; return ((dps / d.stockPrice) * 100).toFixed(2); }, unit: "%", interpret: (v) => `${v}% annual dividend return based on current market price.` },
    ],
  },
];

const defaultData = {
  currentAssets: 120000, currentLiabilities: 80000, inventory: 60000,
  totalDebt: 280000, totalEquity: 240000,
  ebit: 85000, interestExpense: 15000,
  revenue: 1000000, grossProfit: 400000, netProfit: 55000, costOfSales: 600000,
  shares: 100000, stockPrice: 11.00, totalDividends: 30000,
};

function RatioExplorer() {
  const [activeGroup, setActiveGroup] = useState("liquidity");
  const [data, setData] = useState(defaultData);
  const [showInputs, setShowInputs] = useState(false);

  const group = ratioGroups.find(g => g.id === activeGroup);

  const inputFields = [
    ["Current Assets", "currentAssets"], ["Current Liabilities", "currentLiabilities"], ["Inventory", "inventory"],
    ["Total Debt", "totalDebt"], ["Total Equity", "totalEquity"],
    ["EBIT", "ebit"], ["Interest Expense", "interestExpense"],
    ["Revenue", "revenue"], ["Gross Profit", "grossProfit"], ["Net Profit", "netProfit"],
    ["Cost of Sales", "costOfSales"], ["Shares Outstanding", "shares"],
    ["Stock Price ($)", "stockPrice"], ["Total Dividends Paid ($)", "totalDividends"],
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      {/* Group selector */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {ratioGroups.map(g => (
          <button key={g.id} onClick={() => setActiveGroup(g.id)} style={{ padding: "8px 16px", background: activeGroup === g.id ? navy4 : navy3, border: `1px solid ${activeGroup === g.id ? g.color + "55" : border}`, borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: activeGroup === g.id ? g.color : muted, transition: "all 0.2s" }}>{g.icon} {g.label}</button>
        ))}
        <button onClick={() => setShowInputs(!showInputs)} style={{ marginLeft: "auto", padding: "8px 16px", background: showInputs ? navy4 : navy3, border: `1px solid ${showInputs ? "rgba(59,130,246,.4)" : border}`, borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 700, color: showInputs ? blue2 : muted, transition: "all 0.2s" }}>
          {showInputs ? "▲ Hide Inputs" : "✏️ Edit Data"}
        </button>
      </div>

      {/* Editable inputs */}
      {showInputs && (
        <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: muted, marginBottom: 12 }}>Edit Company Data (all ratios update live)</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))", gap: 8 }}>
            {inputFields.map(([lbl, key]) => (
              <div key={key}>
                <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>{lbl}</label>
                <input type="number" value={data[key]} onChange={e => setData({ ...data, [key]: parseFloat(e.target.value) || 0 })}
                  style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 5, padding: "8px 10px", color: white, fontFamily: "monospace", fontSize: "0.82rem", outline: "none" }} />
              </div>
            ))}
          </div>
          <button onClick={() => setData(defaultData)} style={{ marginTop: 12, background: "transparent", color: muted, border: `1px solid ${border}`, padding: "6px 16px", fontFamily: "inherit", fontSize: "0.75rem", borderRadius: 100, cursor: "pointer" }}>↺ Reset to XYZ Inc. defaults</button>
        </div>
      )}

      {/* Ratio cards */}
      <div style={{ background: navy4, border: `1px solid ${group.color}22`, borderRadius: 8, padding: 22 }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: group.color, marginBottom: 4 }}>{group.icon} {group.label} Ratios</div>
        <div style={{ fontSize: "0.82rem", color: muted, marginBottom: 18 }}>{group.desc}</div>
        <div style={{ display: "grid", gap: 12 }}>
          {group.ratios.map((ratio, i) => {
            let val, err = null;
            try { val = ratio.calc(data); } catch { err = "N/A"; }
            const display = err || val;
            return (
              <div key={i} style={{ background: navy3, borderRadius: 8, padding: 18, borderLeft: `3px solid ${group.color}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: "0.88rem", fontWeight: 700, color: white, marginBottom: 2 }}>{ratio.name}</div>
                    <div style={{ fontFamily: "monospace", fontSize: "0.75rem", color: muted }}>{ratio.formula}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontFamily: "monospace", fontSize: "1.4rem", fontWeight: 700, color: group.color }}>{display}</div>
                    <div style={{ fontSize: "0.68rem", color: muted }}>{ratio.unit}</div>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <div style={{ fontSize: "0.78rem", color: textCol, lineHeight: 1.6, flex: 1 }}>{val && ratio.interpret(val)}</div>
                  <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "3px 10px", borderRadius: 100, color: ratio.benchmarkColor, background: `${ratio.benchmarkColor}18`, border: `1px solid ${ratio.benchmarkColor}33`, whiteSpace: "nowrap", flexShrink: 0 }}>{ratio.benchmark}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Retained Earnings Flow ──
function RetainedEarningsFlow() {
  const [opening, setOpening] = useState(30000);
  const [netProfit, setNetProfit] = useState(55000);
  const [dividends, setDividends] = useState(30000);
  const closing = opening + netProfit - dividends;

  const steps = [
    { label: "Opening Retained Earnings", value: opening, color: blue2, sign: "" },
    { label: "Add: Net Profit", value: netProfit, color: green, sign: "+" },
    { label: "Less: Dividends Paid", value: dividends, color: red, sign: "−" },
    { label: "Closing Retained Earnings", value: closing, color: gold, sign: "=" },
  ];

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🔗 Retained Earnings Flow Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.8rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 20 }}>
        Opening RE + Net Profit − Dividends Paid = Closing RE
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginBottom: 20 }}>
        {[["Opening Retained Earnings ($)", opening, setOpening], ["Net Profit ($)", netProfit, setNetProfit], ["Dividends Paid ($)", dividends, setDividends]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" value={val} onChange={e => set(parseFloat(e.target.value) || 0)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {steps.map((step, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, background: i === 3 ? navy4 : navy3, border: `1px solid ${i === 3 ? step.color + "55" : border}`, borderRadius: 8, padding: "13px 18px" }}>
            <span style={{ fontFamily: "monospace", fontSize: "1.1rem", color: step.color, width: 20, flexShrink: 0, textAlign: "center" }}>{step.sign}</span>
            <span style={{ fontSize: "0.85rem", color: i === 3 ? white : textCol, fontWeight: i === 3 ? 700 : 400, flex: 1 }}>{step.label}</span>
            <span style={{ fontFamily: "monospace", fontSize: i === 3 ? "1.1rem" : "0.9rem", fontWeight: i === 3 ? 700 : 400, color: step.color }}>${step.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: "0.8rem", color: muted, marginTop: 12 }}>
        The closing retained earnings (${closing.toLocaleString()}) flows directly onto the <strong style={{ color: textCol }}>Balance Sheet</strong> under Shareholders' Equity.
      </div>
    </div>
  );
}

// ── EPS + P/E Step Calculator ──
function EPSPECalc() {
  const [netProfit, setNetProfit] = useState(55000);
  const [shares, setShares] = useState(100000);
  const [price, setPrice] = useState(11.00);
  const [result, setResult] = useState(null);

  function calc() {
    const np = parseFloat(netProfit), sh = parseFloat(shares), p = parseFloat(price);
    const eps = np / sh;
    const pe = p / eps;
    const dil = pe >= 30 ? { label: "Expensive / Growth Stock", color: red } : pe >= 15 ? { label: "Moderate Valuation", color: gold } : { label: "Cheap / Value Stock", color: green };
    setResult({ np, sh, p, eps: eps.toFixed(2), pe: pe.toFixed(1), dil });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 EPS &amp; P/E Ratio Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.8rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 20, lineHeight: 1.7 }}>
        EPS = Net Profit ÷ Shares Outstanding<br />P/E = Market Price ÷ EPS
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginBottom: 16 }}>
        {[["Net Profit ($)", netProfit, setNetProfit], ["Shares Outstanding", shares, setShares], ["Stock Price ($)", price, setPrice]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="any" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && (
        <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 20, marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            {[["EPS", `$${result.eps}`, purple], ["P/E Ratio", `${result.pe}×`, gold]].map(([lbl, val, col]) => (
              <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "12px 14px", textAlign: "center" }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, marginBottom: 4 }}>{lbl}</div>
                <div style={{ fontFamily: "monospace", fontSize: "1.4rem", fontWeight: 700, color: white }}>{val}</div>
              </div>
            ))}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 10px", borderTop: `1px solid ${border}`, paddingTop: 12 }}>
            {[
              `EPS: $${result.np.toLocaleString()} ÷ ${result.sh.toLocaleString()} shares = $${result.eps}`,
              `P/E: $${result.p} ÷ $${result.eps} = ${result.pe}×`,
              `Investors pay $${result.pe} for every $1 of earnings`,
            ].map((step, i) => (
              <li key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "4px 0", display: "flex", gap: 8 }}>
                <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
              </li>
            ))}
          </ul>
          <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "3px 12px", borderRadius: 100, color: result.dil.color, background: `${result.dil.color}18`, border: `1px solid ${result.dil.color}33` }}>{result.dil.label}</span>
        </div>
      )}
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which financial statement shows a company's financial position at a specific point in time?", opts: ["Statement of Comprehensive Income", "Statement of Changes in Equity", "Statement of Financial Position (Balance Sheet)", "Auditor's Report"], correct: 2, fb: { y: "Correct! The Balance Sheet (Statement of Financial Position) is a snapshot at a specific date. The Income Statement covers a period of time.", n: "Not quite. The Balance Sheet is the 'snapshot' statement — it shows assets, liabilities, and equity at one specific date, not over a period." } },
  { q: "Which of the following is considered a Current Liability?", opts: ["Mortgage due in 5 years", "Inventory", "Trade Payables", "Retained Earnings"], correct: 2, fb: { y: "Correct! Trade Payables (money owed to suppliers) are current liabilities — due within one year. Mortgage is long-term; inventory is an asset; retained earnings is equity.", n: "Not quite. Trade Payables are amounts owed to suppliers within the year — a current liability. Inventory is a current asset. Mortgages are long-term liabilities." } },
  { q: "If a company has Current Assets of $200,000 and Current Liabilities of $100,000, what is the Current Ratio?", opts: ["0.5:1", "1:1", "2:1", "50%"], correct: 2, fb: { y: "Correct! Current Ratio = $200,000 ÷ $100,000 = 2:1. For every $1 of short-term debt, the company has $2 of current assets. This is the 'good' benchmark.", n: "Not quite. Current Ratio = Current Assets ÷ Current Liabilities = $200,000 ÷ $100,000 = 2:1. The standard benchmark is 2:1." } },
  { q: "Retained Earnings are calculated as:", opts: ["Opening Retained Earnings + Profit − Dividends", "Assets − Liabilities", "Revenue − Expenses", "Closing Stock Price × Shares Outstanding"], correct: 0, fb: { y: "Correct! The Statement of Changes in Equity formula: Opening RE + Net Profit − Dividends Paid = Closing RE. This closing figure goes on the Balance Sheet.", n: "Not quite. Retained Earnings = Opening RE + Net Profit − Dividends Paid. This links the Income Statement to the Balance Sheet." } },
  { q: "Which ratio excludes inventory to test immediate liquidity?", opts: ["Current Ratio", "Quick Ratio (Acid Test)", "Debt/Equity Ratio", "Inventory Turnover"], correct: 1, fb: { y: "Correct! The Quick Ratio (Acid Test) removes inventory from current assets before dividing by current liabilities — because inventory may be hard to convert to cash quickly.", n: "Not quite. The Quick Ratio (Acid Test) = (Current Assets − Inventory) ÷ Current Liabilities. It tests whether immediate liquid assets cover short-term debts." } },
  { q: "The Debt/Equity ratio is classified as a:", opts: ["Liquidity Ratio", "Value Ratio", "Risk Analysis (Leverage) Ratio", "Operating Performance Ratio"], correct: 2, fb: { y: "Correct! The Debt/Equity ratio is a leverage (risk analysis) ratio — it shows how much debt is used relative to equity financing. Higher ratios mean higher financial risk.", n: "Not quite. Debt/Equity is a risk/leverage ratio — it measures the degree to which the company is financed by debt vs. equity." } },
  { q: "If Net Profit is $100,000 and Revenue is $1,000,000, what is the Net Profit Margin?", opts: ["1%", "10%", "15%", "100%"], correct: 1, fb: { y: "Correct! Net Profit Margin = $100,000 ÷ $1,000,000 × 100 = 10%. The company keeps 10 cents of every revenue dollar as profit.", n: "Not quite. Net Profit Margin = Net Profit ÷ Revenue × 100 = $100,000 ÷ $1,000,000 × 100 = 10%." } },
  { q: "Depreciation is best described as:", opts: ["A cash expense paid to the government", "The increase in value of land", "An accounting entry allocating the cost of an asset over its useful life", "A current liability"], correct: 2, fb: { y: "Correct! Depreciation is a non-cash accounting entry that spreads the cost of a fixed asset over its useful life — matching the expense to the periods that benefit from the asset.", n: "Not quite. Depreciation is a non-cash charge — it's an accounting allocation of a fixed asset's cost over time, not an actual cash payment." } },
  { q: "A high Inventory Turnover ratio generally indicates:", opts: ["The company is stuck with obsolete goods", "The company is selling its goods quickly and efficiently", "The company has too much debt", "The company is about to go bankrupt"], correct: 1, fb: { y: "Correct! High inventory turnover means the company is cycling through its stock quickly — a sign of strong sales and efficient inventory management.", n: "Not quite. High inventory turnover is positive — it means goods are selling fast, reducing the risk of obsolescence and tying up less cash in unsold stock." } },
  { q: "The P/E Ratio links the stock's market price to:", opts: ["The dividends paid", "The company's total assets", "The earnings per share (EPS)", "The gross revenue"], correct: 2, fb: { y: "Correct! P/E = Market Price ÷ EPS. It shows how much investors are willing to pay for each $1 of the company's earnings — a core valuation metric.", n: "Not quite. P/E = Market Price ÷ Earnings Per Share. It tells you what multiple of earnings investors are paying for the stock." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;
  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }
  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 9 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered financial statement analysis." : score >= 8 ? "Excellent. Review the questions you missed." : score >= 6 ? "Good start. Revisit the ratio categories and formulas." : "Go back through the statements and ratios — focus on the four categories."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Balance Sheet", def: "Statement of Financial Position — a snapshot of assets, liabilities, and shareholders' equity at a specific date." },
  { term: "Income Statement", def: "Statement of Comprehensive Income — shows revenue, costs, and net profit over a period (e.g., one year)." },
  { term: "Fundamental Accounting Equation", def: "Assets = Liabilities + Shareholders' Equity. The foundation of the balance sheet — always balanced." },
  { term: "Current Assets", def: "Assets expected to be converted to cash within one year: cash, receivables, inventory." },
  { term: "Fixed Assets", def: "Long-term assets used in operations (factories, equipment, land). Shown net of accumulated depreciation." },
  { term: "Depreciation", def: "Non-cash accounting entry allocating the cost of a fixed asset over its useful life. Reduces carrying value." },
  { term: "Current Liabilities", def: "Debts due within one year: trade payables, wages payable, taxes payable." },
  { term: "Retained Earnings", def: "Accumulated profits kept in the business — not paid as dividends. Opening RE + Net Profit − Dividends = Closing RE." },
  { term: "Gross Profit", def: "Revenue minus Cost of Sales. Measures efficiency of production before overhead and admin costs." },
  { term: "Net Profit", def: "The 'bottom line' — gross profit minus all expenses, interest, and taxes." },
  { term: "Current Ratio", def: "Current Assets ÷ Current Liabilities. Measures short-term solvency. Benchmark: 2:1." },
  { term: "Quick Ratio (Acid Test)", def: "(Current Assets − Inventory) ÷ Current Liabilities. Stricter liquidity test. Benchmark: 1:1." },
  { term: "Debt / Equity Ratio", def: "Total Debt ÷ Total Equity. Measures financial leverage — higher means more risk." },
  { term: "Interest Coverage Ratio", def: "EBIT ÷ Interest Expense. Measures ability to pay interest — higher is safer." },
  { term: "Gross Profit Margin", def: "Gross Profit ÷ Revenue × 100. Measures production efficiency. Higher is better." },
  { term: "Net Profit Margin", def: "Net Profit ÷ Revenue × 100. Overall profitability after all costs. Higher is better." },
  { term: "Return on Equity (ROE)", def: "Net Profit ÷ Total Equity × 100. Return generated on shareholders' invested capital." },
  { term: "Inventory Turnover", def: "Cost of Sales ÷ Inventory. How many times inventory is sold per year. Higher = more efficient." },
  { term: "Earnings Per Share (EPS)", def: "Net Profit ÷ Shares Outstanding. Profit attributable to each share." },
  { term: "Price-Earnings Ratio (P/E)", def: "Market Price ÷ EPS. How much investors pay for $1 of earnings. Core valuation metric." },
  { term: "Dividend Yield", def: "Annual Dividend Per Share ÷ Market Price × 100. Annual income return from dividends as a % of price." },
];

// ── Main Export ──
export default function Chapter9() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Overview of the Three Financial Statements"],
    ["02", "Statement of Financial Position (Balance Sheet)"],
    ["03", "Statement of Comprehensive Income"],
    ["04", "Statement of Changes in Equity"],
    ["05", "Ratio Analysis — All Four Categories"],
    ["🧮", "Live Ratio Explorer, EPS/P/E & Retained Earnings Calculators"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        {/* HERO */}
        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 9
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Financial Statement <span style={{ color: gold }}>Analysis</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              The three core financial statements, the accounting equation, and all 10 financial ratios — with a live ratio explorer where you can input any company's data and see every ratio update in real time.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","5","Core Topics"],["⏱","~20 min","read"],["🎯","10","practice questions"],["📊","10","financial ratios"],["🧮","3","interactive tools"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* TOC */}
        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* ── S1: Overview ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 01</span><span style={s.badge}>Foundations</span><div style={s.line} /></div>
            <h2 style={s.h2}>Overview of Financial Statements</h2>
            <p style={s.prose}>
              Fund managers use <span style={{ color: gold, fontWeight: 600 }}>Fundamental Analysis</span> to determine the "true" or intrinsic value of a company. If the true value exceeds the current market price, the stock is <strong style={{ color: white }}>undervalued</strong> — a potential buying opportunity. This analysis starts with the three core financial statements every public company must produce:
            </p>
          </Reveal>
          <Reveal delay={0.1}><StatementsOverview /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="🔗">
              <strong>How the Statements Connect:</strong> The <strong>Income Statement</strong> produces Net Profit → which flows into the <strong>Statement of Changes in Equity</strong> → which produces Closing Retained Earnings → which appears on the <strong>Balance Sheet</strong>. They are three interconnected views of the same business.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S2: Balance Sheet ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 02</span><span style={s.badge}>Balance Sheet</span><div style={s.line} /></div>
            <h2 style={s.h2}>The Balance Sheet</h2>
            <p style={s.prose}>
              The Balance Sheet is built on the <span style={{ color: gold, fontWeight: 600 }}>fundamental accounting equation: Assets = Liabilities + Shareholders' Equity</span>. It is always perfectly balanced. Below is an annotated, interactive example using XYZ Inc.'s data:
            </p>
          </Reveal>
          <Reveal delay={0.1}><BalanceSheet /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="📌" accent={blue}>
              <strong style={{ color: blue2 }}>Key Exam Distinctions:</strong> <strong>Current</strong> assets/liabilities are due or convertible within 1 year. <strong>Non-current</strong> (fixed/long-term) are beyond 1 year. <strong>Depreciation</strong> is a non-cash charge — it reduces the carrying value of fixed assets over time but doesn't involve a cash payment.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S3+4: Income + Equity ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Sections 03 &amp; 04</span><span style={s.badge}>Income & Equity</span><div style={s.line} /></div>
            <h2 style={s.h2}>Income Statement &amp; Changes in Equity</h2>
            <p style={s.prose}>
              The Income Statement tracks profitability over a period — <span style={{ color: gold, fontWeight: 600 }}>Revenue → Gross Profit → Net Profit</span>. The Statement of Changes in Equity then takes that Net Profit and calculates the new Retained Earnings balance for the Balance Sheet. Use the live calculator below:
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, margin: "0 0 28px" }}>
              {[
                { step: "Revenue", desc: "Total sales of goods or services", sub: "$1,000,000", color: green },
                { step: "− Cost of Sales", desc: "Direct production costs (labour, materials)", sub: "$600,000", color: red },
                { step: "= Gross Profit", desc: "Efficiency of production", sub: "$400,000", color: blue2 },
                { step: "− Expenses & Tax", desc: "Rent, salaries, depreciation, interest, tax", sub: "$345,000", color: red },
                { step: "= Net Profit", desc: "The bottom line — flows to equity statement", sub: "$55,000", color: gold },
              ].map((item, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "16px 18px", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: item.color }} />
                  <div style={{ fontFamily: "monospace", fontSize: "0.75rem", color: item.color, fontWeight: 700, marginBottom: 4 }}>{item.step}</div>
                  <div style={{ fontSize: "0.82rem", color: muted, marginBottom: 6 }}>{item.desc}</div>
                  <div style={{ fontFamily: "monospace", fontSize: "1rem", color: white }}>{item.sub}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}><RetainedEarningsFlow /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S5: Ratios ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 05</span><span style={s.badge}>Ratio Analysis</span><div style={s.line} /></div>
            <h2 style={s.h2}>Financial Ratio Analysis</h2>
            <p style={s.prose}>
              Ratios allow analysts to compare a company's performance over time (trend analysis) or against peers (industry benchmarking). There are <span style={{ color: gold, fontWeight: 600 }}>four categories of ratios</span> — each answering a different question about the company's health. The explorer below uses XYZ Inc.'s data by default but you can edit any input to see all ratios update live:
            </p>
          </Reveal>
          <Reveal delay={0.1}><RatioExplorer /></Reveal>
          <Reveal delay={0.2}><EPSPECalc /></Reveal>
          <Reveal delay={0.3}>
            <Callout icon="📐" accent={purple}>
              <strong style={{ color: purple }}>Ratio Category Cheat Sheet:</strong><br />
              <strong>Liquidity</strong> — Can it pay short-term debts? (Current Ratio, Quick Ratio)<br />
              <strong>Risk/Leverage</strong> — Can it handle its debt? (Debt/Equity, Interest Coverage)<br />
              <strong>Operating Performance</strong> — How efficiently is it run? (Gross Margin, Net Margin, ROE, Inventory Turnover)<br />
              <strong>Value</strong> — What is the stock worth? (EPS, P/E Ratio, Dividend Yield)
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Quiz ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Knowledge Check</span><span style={s.badge}>10 Questions</span><div style={s.line} /></div>
            <h2 style={s.h2}>Test Your Understanding</h2>
            <p style={s.prose}>10 questions covering financial statements, the accounting equation, and all four ratio categories from Chapter 9.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Glossary ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Quick Reference</span><span style={s.badge}>Key Terms</span><div style={s.line} /></div>
            <h2 style={s.h2}>Glossary</h2>
            <p style={s.prose}>All essential terms from Chapter 9 — financial statements and the 10 key ratios.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
